import ExclamationIcon from '@heroicons/react/outline/ExclamationIcon'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { BAR_ADDRESS, ChainId, SELF, SELF_ADDRESS, ZERO } from 'sdk'
import Container from 'app/components/Container'
import Dots from 'app/components/Dots'
import InputType from 'app/components/Input/Numeric/slow'
import Web3Connect from 'app/components/Web3Connect'
import { XSUSHI } from 'app/config/tokens'
import { classNames, getExplorerLink } from 'app/functions'
import { aprToApy } from 'app/functions/convert/apyApr'
import { tryParseAmount } from 'app/functions/parse'
import { ApprovalState, useApproveCallback } from 'app/hooks/useApproveCallback'
import useSushiBarSlow from 'app/hooks/useSushiBarSlow'
import TransactionFailedModal from 'app/modals/TransactionFailedModal'
import { useFactory, useNativePrice, useOneDayBlock, useTokens } from 'app/services/graph/hooks'
import { useBar } from 'app/services/graph/hooks/bar'
import { useActiveWeb3React } from 'app/services/web3'
import { useWalletModalToggle } from 'app/state/application/hooks'
import { useTokenBalance } from 'app/state/wallet/hooks'
import Head from 'next/head'
import {useEffect, useRef} from 'react';
import React, { useState } from 'react'
import { shortenString } from 'app/functions/format'
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  GridItem,
  Button,
  Image,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useDisclosure,
  ModalCloseButton,
  ModalBody,
  useColorMode,
} from '@chakra-ui/react'

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
} from '@chakra-ui/react'
import ExternalLink from 'app/components/ExternalLink'
const INPUT_CHAR_LIMIT = 18

const sendTx = async (txFunc: () => Promise<any>) => {
  let success = true
  try {
    const ret = await txFunc()
    if (ret?.error) {
      success = false
    }
    else if(ret?.code == 4001){
      success = false
    }
  } catch (e) {
    console.error(e)
    success = false
  }
  return success
}

const tabStyle = 'flex justify-center items-center h-full w-full rounded-lg cursor-pointer text-sm md:text-base'
const activeTabStyle = `${tabStyle} text-high-emphesis font-bold bg-dark-900`
const inactiveTabStyle = `${tabStyle} text-secondary`

const buttonStyle =
  'hrvst_btn flex justify-center items-center w-full h-14 rounded font-bold md:font-medium md:text-lg mt-5 text-sm focus:outline-none focus:ring'
const buttonStyleEnabled = `${buttonStyle} text-high-emphesis`
const buttonStyleInsufficientFunds = `${buttonStyleEnabled} opacity-60`
const buttonStyleDisabled = `${buttonStyle} text-white bg-dark-700 opacity-50`
const buttonStyleConnectWallet = `${buttonStyle} text-high-emphesis bg-blue hover:bg-opacity-90`

export default function Staking() {
  const { i18n } = useLingui()
  const { account, chainId } = useActiveWeb3React()
  const sushiBalance = useTokenBalance(account ?? undefined, SELF[ChainId.AVALANCHE])
  const xSushiBalance = useTokenBalance(account ?? undefined, XSUSHI)
  const { isOpen: isConfirmationOpen, onOpen: onConfirmationOpen, onClose: onConfirmationClose } = useDisclosure()
  const { isOpen: isConfirmationTrxOpen, onOpen: onConfirmationTrxOpen, onClose: onConfirmationTrxClose } = useDisclosure()
  const { isOpen: isTrxRejectOpen, onOpen: onTrxRejectOpen, onClose: onTrxRejectClose } = useDisclosure()
  const { isOpen: isTrxSubmittedOpen, onOpen: onTrxSubmittedOpen, onClose: onTrxSubmittedClose } = useDisclosure()
  const [txHash, setTxHash] = useState<string>('')

  const { colorMode, toggleColorMode } = useColorMode()
  const { enter, leave } = useSushiBarSlow({setTxHash:setTxHash})

  const walletConnected = !!account
  const toggleWalletModal = useWalletModalToggle()

  const [activeTab, setActiveTab] = useState(0)
  const [modalOpen, setModalOpen] = useState(false)

  const [input, setInput] = useState<string>('')
  const [inputWidth, setInputWidth] = useState<number>(35)
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const ref = React.useRef<any>()
  const ref2 = React.useRef<any>()

  useEffect(() => {
    if(ref){
      ref.current.focus()
    }
  }, [ref])

  useEffect(() => {
    if(ref2){
      ref2.current.focus()
    }
  }, [ref2])
  const [usingBalance, setUsingBalance] = useState(false)
  const balance = activeTab === 0 ? sushiBalance : xSushiBalance

  const formattedBalance = shortenString(balance?.toSignificant(4), 6)

  const parsedAmount = usingBalance ? balance : tryParseAmount(input, balance?.currency)
  const [approvalState, approve] = useApproveCallback(parsedAmount, BAR_ADDRESS[ChainId.AVALANCHE])

  const handleInput = (v: string) => {
    if (v.length <= INPUT_CHAR_LIMIT) {
      setUsingBalance(false)
      setInput(v)

      if(v.length == 0){
        setInputWidth(35)
      }
      else{
        setInputWidth(35 + (10 * v.length))
      }
    }
  }

  const handleClickMax = () => {
    // @ts-ignore TYPE NEEDS FIXING
    // setInput(parsedAmount ? parsedAmount.toSignificant(balance.currency.decimals).substring(0, INPUT_CHAR_LIMIT) : '')
    // setInput(balance?.toSignificant(balance.currency.decimals).substring(0, INPUT_CHAR_LIMIT))
    let input = balance?.toSignificant(14).substring(0, INPUT_CHAR_LIMIT);

    setInput(input)
    setUsingBalance(true)
    setInputWidth(35 + (10 * input.length))
  }

  // @ts-ignore TYPE NEEDS FIXING
  const insufficientFunds = (balance && balance.equalTo(ZERO)) || parsedAmount?.greaterThan(balance)

  const inputError = insufficientFunds

  const [pendingTx, setPendingTx] = useState(false)

  const buttonDisabled = !input || pendingTx || (parsedAmount && parsedAmount.equalTo(ZERO))

  const handleClickButton = async () => {
    if (buttonDisabled) return

    if (!walletConnected) {
      toggleWalletModal()
    } else {
      setPendingTx(true)

      onConfirmationClose(); 
      onConfirmationTrxOpen();

      if (activeTab === 0) {
        if (approvalState === ApprovalState.NOT_APPROVED) {
          const success = await sendTx(() => approve())
          if (!success) {
            setPendingTx(false)
            onConfirmationTrxClose()
            onTrxRejectOpen()
            setTxHash('')
            // setModalOpen(true)
            return
          }
          else{
            onConfirmationTrxClose()
            onTrxSubmittedOpen()
          }
        }
        const success = await sendTx(() => enter(parsedAmount))
        if (!success) {
          setPendingTx(false)
          onConfirmationTrxClose()
          onTrxRejectOpen()
          setTxHash('')
          // setModalOpen(true)
          return
        }
        else{
          // setTxHash(trxHash)
          onConfirmationTrxClose()
          onTrxSubmittedOpen()
        }
      } else if (activeTab === 1) {
        const success = await sendTx(() => leave(parsedAmount))
        if (!success) {
          setPendingTx(false)
          onConfirmationTrxClose()
          onTrxRejectOpen()
          setTxHash('')
          // setModalOpen(true)
          return
        }
        else{
          // setTxHash(trxHash)
          onConfirmationTrxClose()
          onTrxSubmittedOpen()
        }
      }

      handleInput('')
      setPendingTx(false)
    }
  }

  const block1d = useOneDayBlock({ chainId: ChainId.AVALANCHE })
  const exchange = useFactory({ chainId: ChainId.AVALANCHE })

  const exchange1d = useFactory({
    chainId: ChainId.AVALANCHE,
    variables: {
      block: block1d,
    },
    shouldFetch: !!block1d,
  })

  const ethPrice = useNativePrice({ chainId: ChainId.AVALANCHE })

  const xSushi = useTokens({
    chainId: ChainId.AVALANCHE,
    variables: { where: { id: XSUSHI.address.toLowerCase() } },
  })?.[0]
  // console.log("aprToApy-xSushi: ", xSushi)

  const sushi = useTokens({
    chainId: ChainId.AVALANCHE,
    variables: { where: { id: SELF_ADDRESS[ChainId.AVALANCHE].toLowerCase() } },
  })?.[0]
  // console.log("aprToApy-sushi: ", sushi)

  const bar = useBar()
  // console.log("aprToApy-bar: ", bar)

  // const [xSushiPrice] = [xSushi?.derivedETH * ethPrice, xSushi?.derivedETH * ethPrice * bar?.totalSupply]
  const xSushiPrice = sushi?.derivedETH * ethPrice * bar?.ratio
  // console.log("aprToApy-sushi?.derivedETH: ", sushi?.derivedETH)
  // console.log("aprToApy-ethPrice: ", ethPrice)
  // console.log("aprToApy-bar?.ratio: ", bar?.ratio)
  // console.log("aprToApy-xSushiPrice: ", xSushiPrice)

  const APY1d = aprToApy(
    (((exchange?.volumeUSD - exchange1d?.volumeUSD) * 0.0005 * 365.25) / (bar?.totalSupply * xSushiPrice)) * 100 ?? 0
  )

  return (
    <>
      <Box className="staking_apr_main">
        <Heading as="h3" className="stking_apr_h">
          Staking
        </Heading>
        <Box className="stkng_apr_row">
          <Heading as="h2" className="stkng_apr_h2">
            {i18n._(t`Staking APR`)}{' '}
          </Heading>
          <Box className="ldng_ystrdy_text">
            <Text>{`${APY1d ? APY1d.toFixed(2) + '%' : i18n._(t`Loading...`)}`}</Text>
            <Text className="btm_p">{i18n._(t`Yesterday's APR`)}</Text>
          </Box>
        </Box>
        <Box className="stak_ballnc_box">
          <SimpleGrid columns={12} columnGap={3} rowGap={3}>
            <GridItem colSpan={[12, 12, 8, 8]}>
              <Box className="stak_unstk_tab_bx">
                <Tabs variant="unstyled">
                  <TabList>
                    <Tab
                      className="box_shadov_none_btn"
                      _selected={{ color: 'white', bg: 'blue.500' }}
                      onClick={() => {
                        setActiveTab(0)
                        handleInput('')
                        ref.current.focus()
                      }}
                    >
                      {i18n._(t`Stake OVX`)}
                    </Tab>
                    <Tab
                      className="box_shadov_none_btn"
                      _selected={{ color: 'white', bg: 'green.400' }}
                      onClick={() => {
                        setActiveTab(1)
                        handleInput('')
                        ref2.current.focus()
                      }}
                    >
                      {i18n._(t`Unstake`)}
                    </Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      <Box className="stak_tab_panel">
                        <Box className="stak_tab_panel stak_tab_panel_02">
                          <Box className="raw_bx_as">
                            <Heading as="h6" className="slw_txt_h6">
                              {activeTab === 0 ? i18n._(t`Stake OVX`) : i18n._(t`Unstake`)}
                            </Heading>
                            <Text className="slw_txt_p">{`1 sOVX = ${Number(bar?.ratio ?? 1)?.toFixed(5)} OVX`}</Text>
                          </Box>

                          <Box className='raw_bx_as_prnt' 
                          onClick={e => {ref.current.focus()}}
                          >
                              <Box className='raw_bx_as' style={{gap:"10px"}}>
                                  <Heading as="h6" className='slw_txt_h6'>
                                      <InputType
                                        ref={ref}
                                        value={input}
                                        style={{width:inputWidth+"px"}}
                                        onUserInput={handleInput}
                                        className={"slow_inpt"}
                                        placeholder=" "
                                        // ref={ref}
                                      />
                                      OVX
                                  </Heading>
                                  <Text className='slw_txt_p'>{i18n._(t`Balance`)}: {formattedBalance} <Button className='max_btn box_shadov_none_btn' onClick={handleClickMax}>MAX</Button></Text>
                              </Box>
                          </Box>
                         
                          {/* <Box className="relative w-full h-0 pointer-events-none bottom-14 raw_bx_as_prnt">
                            <Box
                              className={`raw_bx_as flex justify-between items-center h-14 rounded px-3 md:px-5 ${
                                inputError ? '' : ''
                              }`}
                              style={{ borderRadius: '60px' }}
                            >
                              <Heading as="h6" className="slw_txt_h6">
                                {`${input ? input : '0'} ${activeTab === 0 ? '' : 's'}OVX`}
                              </Heading>
                              <Text className="slw_txt_p">
                                {i18n._(t`Balance`)}: {formattedBalance}
                                <Button
                                  className="px-2 py-1 ml-3 text-xs font-bold border pointer-events-auto max_btn box_shadov_none_btn focus:outline-none focus:ring hover:bg-opacity-40 md:bg-opacity-30 border-secondary md:border-white rounded-2xl md:py-1 md:px-3 md:ml-4 md:text-sm md:font-normal md:text-white"
                                  onClick={handleClickMax}
                                >
                                  {i18n._(t`MAX`)}
                                </Button>
                              </Text>
                            </Box>
                          </Box> */}
                          <Box className="hrvst_btn_prnt">
                            {(approvalState === ApprovalState.NOT_APPROVED ||
                              approvalState === ApprovalState.PENDING) &&
                            activeTab === 0 ? (
                              <Button
                                className={`${buttonStyle} text-high-emphesis Button`}
                                disabled={approvalState === ApprovalState.PENDING}
                                onClick={approve}
                              >
                                {approvalState === ApprovalState.PENDING ? (
                                  <Dots>{i18n._(t`Approving`)} </Dots>
                                ) : (
                                  i18n._(t`Approve`)
                                )}
                              </Button>
                            ) : !account ? (
                              <Web3Connect className={buttonStyleConnectWallet} />
                            ) : (
                              <button
                                className={
                                  buttonDisabled
                                    ? buttonStyleDisabled
                                    : insufficientFunds
                                    ? buttonStyleInsufficientFunds
                                    : buttonStyleEnabled
                                }
                                onClick={handleClickButton}
                                disabled={buttonDisabled || inputError}
                              >
                                {!input
                                  ? i18n._(t`Enter Amount`)
                                  : insufficientFunds
                                  ? i18n._(t`Insufficient Balance`)
                                  : activeTab === 0
                                  ? i18n._(t`Confirm Staking`)
                                  : i18n._(t`Claim Rewards`)}
                              </button>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </TabPanel>
                    <TabPanel>
                      <Box className="stak_tab_panel">
                        <Box className="stak_tab_panel stak_tab_panel_02">
                          <Box className="raw_bx_as">
                            <Heading as="h6" className="slw_txt_h6">
                              {activeTab === 0 ? i18n._(t`Stake OVX`) : i18n._(t`Unstake`)}
                            </Heading>
                            <Text className="slw_txt_p">{`1 sOVX = ${Number(bar?.ratio ?? 1)?.toFixed(5)} OVX`}</Text>
                          </Box>


                          <Box className='raw_bx_as_prnt' 
                          onClick={e => {ref2.current.focus()}}
                          >
                              <Box className='raw_bx_as'>
                                  <Heading as="h6" className='slw_txt_h6'>
                                      <InputType
                                        ref={ref2}
                                        value={input}
                                        style={{width:inputWidth+"px"}}
                                        onUserInput={handleInput}
                                        className={"slow_inpt"}
                                        placeholder=" "
                                        // ref={ref}
                                      />
                                      sOVX
                                  </Heading>
                                  <Text className='slw_txt_p'>{i18n._(t`Balance`)}: {formattedBalance} <Button className='max_btn box_shadov_none_btn' onClick={handleClickMax}>MAX</Button></Text>
                              </Box>
                          </Box>
                          {/* <InputType
                            value={input}
                            onUserInput={handleInput}
                            // className={classNames(
                            //   'w-full h-14 px-3 md:px-5 mt-5 rounded bg-dark-800 text-sm md:text-lg font-bold text-dark-800 whitespace-nowrap caret-high-emphesis',
                            //   inputError ? ' pl-9 md:pl-12' : ''
                            // )}
                            placeholder=" "
                          />
                          <Box className="relative w-full h-0 pointer-events-none bottom-14 raw_bx_as_prnt">
                            <Box
                              className={`raw_bx_as flex justify-between items-center h-14 rounded px-3 md:px-5 ${
                                inputError ? '' : ''
                              }`}
                              style={{ borderRadius: '60px' }}
                            >
                              <Heading as="h6" className="slw_txt_h6">
                                {`${input ? input : '0'} ${activeTab === 0 ? '' : 's'}OVX`}
                              </Heading>
                              <Text className="slw_txt_p">
                                {i18n._(t`Balance`)}: {formattedBalance}
                                <Button
                                  className="px-2 py-1 ml-3 text-xs font-bold border pointer-events-auto max_btn box_shadov_none_btn focus:outline-none focus:ring hover:bg-opacity-40 md:bg-opacity-30 border-secondary md:border-white rounded-2xl md:py-1 md:px-3 md:ml-4 md:text-sm md:font-normal md:text-white"
                                  onClick={handleClickMax}
                                >
                                  {i18n._(t`MAX`)}
                                </Button>
                              </Text>
                            </Box>
                          </Box> */}
                          <Box className="hrvst_btn_prnt">
                            {(approvalState === ApprovalState.NOT_APPROVED ||
                              approvalState === ApprovalState.PENDING) &&
                            activeTab === 0 ? (
                              <Button
                                className={`${buttonStyle} text-high-emphesis Button`}
                                disabled={approvalState === ApprovalState.PENDING}
                                onClick={approve}
                              >
                                {approvalState === ApprovalState.PENDING ? (
                                  <Dots>{i18n._(t`Approving`)} </Dots>
                                ) : (
                                  i18n._(t`Approve`)
                                )}
                              </Button>
                            ) : !account ? (
                              <Web3Connect className={buttonStyleConnectWallet} />
                            ) : (
                              <button
                                className={
                                  buttonDisabled
                                    ? buttonStyleDisabled
                                    : insufficientFunds
                                    ? buttonStyleInsufficientFunds
                                    : buttonStyleEnabled
                                }
                                onClick={handleClickButton}
                                disabled={buttonDisabled || inputError}
                              >
                                {!input
                                  ? i18n._(t`Enter Amount`)
                                  : insufficientFunds
                                  ? i18n._(t`Insufficient Balance`)
                                  : activeTab === 0
                                  ? i18n._(t`Confirm Staking`)
                                  : i18n._(t`Claim Rewards`)}
                              </button>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Box>
            </GridItem>
            <GridItem colSpan={[12, 12, 4, 4]}>
              <Box className="blnc_unstkd_box">
                <Box className="blnc_bx_inn">
                  <Heading as="h5">{i18n._(t`Balance`)}</Heading>
                  <Box className="img_text_box">
                    <Box className="img_radius_bx">
                      <Image src="/img/slow_bg_swap.svg" className="slow_bg_swap_as" alt="" />
                    </Box>
                    <Box>
                      <Text>{xSushiBalance ? shortenString(xSushiBalance.toSignificant(4), 6) : '-'}</Text>
                      <Text className="lite_p">sOVX</Text>
                    </Box>
                  </Box>
                </Box>
                <Box className="blnc_bx_inn blnc_bx_inn_btm">
                  <Heading as="h5">{i18n._(t`Unstaked`)}</Heading>
                  <Box className="img_text_box">
                    <Box className="img_radius_bx">
                      <Image src="/img/slow_bg_swap.svg" className="slow_bg_swap_as" alt="" />
                    </Box>
                    <Box>
                      <Text>{sushiBalance ? shortenString(sushiBalance.toSignificant(4), 6) : '-'}</Text>
                      <Text className="lite_p">OVX</Text>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </GridItem>
          </SimpleGrid>
        </Box>
      </Box>

      <Modal isOpen={isConfirmationOpen} onClose={onConfirmationClose} isCentered>
          <ModalOverlay />
          <ModalContent className="stake_confirmation_modal add_liquidty_modal_as_mng_lqdt">
            <Box className="add_liquidty_modal_cntnt">
              <ModalHeader>
                <Text className="mng_lqdt_tx">Stake Confirmation</Text>
              </ModalHeader>
              {/* <ModalCloseButton /> */}
              <Box className="add_liquidty_mdl_body">
                <Box className="text_row">
                  <Text>Are you sure you want to stake:</Text>
                </Box>
                
                <Box className='black_raw_box_as'>
                    
                  <Box className="img_text_box">
                    <Box className="img_radius_bx">
                      <Image src="/img/slow_bg_swap.svg" className="slow_bg_swap_as" alt="" />
                    </Box>
                    <Box>
                      <Text>{input ? input : '-'}</Text>
                      {/* <Text>$0.00</Text> */}

                      <Text className="lite_p">OVX</Text>
                    </Box>
                    {/* <Box className='text_row'>
                      <Text className="lite_p">OVX</Text>
                    </Box> */}
                  </Box>

                    {/* <Image src="/img/slow_bg_swap.svg" className="slow_bg_swap_as" alt="" />
                    <Box className='text_row'>
                        <Text>Your pool share</Text>
                        <Text>8.005246%</Text>
                    </Box>
                    <Box className='text_row'>
                        <Text>OVX</Text>
                    </Box> */}
                    
                </Box>
              </Box>

              <Box className="add_remov_btns_prnt_as">
                <Button onClick={handleClickButton} className="add_btn box_shadov_none_btn">
                  Confirm Stake
                </Button>
                <Button className="cancel" onClick={onConfirmationClose}>Cancel</Button>
              </Box>
            </Box>
          </ModalContent>
      </Modal>

      <Modal isOpen={isConfirmationTrxOpen} onClose={onConfirmationTrxClose} isCentered>
        <ModalOverlay />
        <ModalContent className="swap_main_perant">
          <Box className="swap_popup">
            <Box className="tex_box">
              <Text>Confirm transaction</Text>
              <ModalCloseButton />
            </Box>
            <ModalBody className="modl_bdy_susi_ppup">
              <Box className="middle_img_bx">
                {colorMode === 'light' ? (
                  <Image src="/img/confirm_transaction.svg" alt="" />
                ) : (
                  <Image src="/img/confirm_transaction_dark.svg" alt="" />
                )}
              </Box>
              <Box className="p_h_mx_sushi">
                <Text>Waiting for confirmation Confirm this transaction in your wallet</Text>
                {/* <Heading as="h2">Swapping 0.0 SUSHI for 0.0227334 AVAX</Heading> */}
              </Box>
              {/* <Box className="middle_img_bx">
                <Image src="/img/middle_sushi_img_2.png" alt="" />
              </Box>
              <Box className="h_p_mix_sushi">
                <Heading as="h4">Transaction Submitted</Heading>
                <Text>View on explorer</Text>
                <Button>Add Sushi to Metamask</Button>
              </Box> */}
            </ModalBody>
          </Box>
        </ModalContent>
      </Modal>


      <Modal isOpen={isTrxRejectOpen} onClose={onTrxRejectClose} isCentered>
        <ModalOverlay />
        <ModalContent className="swap_main_perant">
          <Box className="swap_popup trans_rej_popp">
            <Box className="tex_box">
              <Text>Transaction Rejected</Text>
              <ModalCloseButton />
            </Box>
            <ModalBody className="modl_bdy_susi_ppup">
              <Box className="middle_img_bx">
                {colorMode === 'light' ? (
                  <Image src="/img/cencel_img.png" alt="" />
                ) : (
                  <Image src="/img/cencel_img02.png" alt="" />
                )}
              </Box>
              <Box className=" h_p_mix_sushi h_p_mix_oopas">
                <Heading as="h4">Oops!</Heading>
                <Text>Transaction rejected</Text>
                <Button className="cmmnbtn" onClick={onTrxRejectClose}>Dismiss</Button>
              </Box>
            </ModalBody>
          </Box>
        </ModalContent>
      </Modal>

      <Modal isOpen={isTrxSubmittedOpen} onClose={onTrxSubmittedClose} isCentered>
        <ModalOverlay />
        <ModalContent className="swap_main_perant">
          <Box className="swap_popup">
            <Box className="tex_box">
              <Text>Transaction Submitted</Text>
              <ModalCloseButton />
            </Box>
            <ModalBody className="modl_bdy_susi_ppup">
              {/* <Box className="middle_img_bx">
                <Image src="/img/middle_sushi_img.png" alt="" />
              </Box>
              <Box className="p_h_mx_sushi">
                <Text>Waiting for confirmation Confirm this transaction in your wallet</Text>
                <Heading as="h2">Swapping 0.0 SUSHI for 0.0227334 AVAX</Heading>
              </Box> */}
              <Box className="middle_img_bx">
                
                {colorMode === 'light' ? (
                  <Image src="/img/transaction_submitted.svg" alt="" />
                ) : (
                  <Image src="/img/transaction_submitted_dark.svg" alt="" />
                )}
              </Box>
              <Box className="h_p_mix_sushi">
                <Heading as="h4">Transaction Submitted</Heading>

                <ExternalLink href={getExplorerLink(chainId, txHash, 'transaction')}>
                  <Text>{i18n._(t`View on explorer`)}</Text>
                </ExternalLink>
                {/* <Text>View on explorer</Text> */}
                {/* <Button>Add Sushi to Metamask</Button> */}
              </Box>
            </ModalBody>
          </Box>
        </ModalContent>
      </Modal>
    </>
  )
}
